// Third-party packages
import {
	isOnline, onNetworkStatusChange,
} from '@helpers/networkStatus';
import {
	AppDescription, Destination, IAnalytics,
} from '@local_types/index';

// Internal libraries (@stonal-tech/*)
import {
	getCurrentOrganization,
} from '@stonal-tech/lib-front-utils-ts';

// Project modules
import {
	logger,
} from '@config/logger';

// Relative imports
import {
	MAX_BUFFER_SIZE,
} from '../config/networkStatus';

// Vite injects BUILD_VERSION at build time

// @ts-ignore
declare const BUILD_VERSION: string;

/**
 * Get the app version from the page's meta tag
 */
function getAppVersion(): string | undefined {
	const metaVersion = document.querySelector('meta[name="version"]');
	return metaVersion?.getAttribute('content') || undefined;
}

/**
 * Represents a buffered event stored while offline
 */
type BufferedEvent =
	| { type: 'track';
		event: string;
		properties: Record<string, any> }
	| { type: 'page';
		name: string;
		properties: Record<string, any> }
	| { type: 'identify';
		userId: string | undefined;
		traits: Record<string, any> }
	| { type: 'group';
		groupId: string;
		traits: Record<string, any> };

/**
 * Core analytics object
 *
 * This is the main object that allows to track events and identify users.
 * When offline, events are buffered in memory and replayed when connectivity returns.
 *
 */
export class Analytics implements IAnalytics {
	private destinations: Destination[] = [];
	private app: AppDescription | undefined = undefined;
	private appVersion: string | undefined = undefined;
	private currentOrg: string | undefined = undefined;
	private userGroupsByOrg: Record<string, string[]> | undefined = undefined;
	private offlineBuffer: BufferedEvent[] = [];
	private _isOffline: boolean = false;

	constructor() {
		this.appVersion = getAppVersion();
		this._isOffline = !isOnline();
		// Eagerly restore userGroupsByOrg from localStorage cache
		// so that page events fired before identCheck.run() already have groups
		try {
			const cached = localStorage.getItem('stn_tr_user_groups_by_org');
			if (cached) {
				this.userGroupsByOrg = JSON.parse(cached);
			}
		} catch { /* ignore */ }
		onNetworkStatusChange((online) => {
			this._isOffline = !online;
			if (online) {
				logger.info('Network restored, flushing offline buffer (' + this.offlineBuffer.length + ' events)');
				this._flushBuffer();
			} else {
				logger.info('Network lost, buffering events');
			}
		});
	}

	setApp(app: AppDescription | undefined) {
		if (app === undefined) {
			return;
		}

		this.app = app;
		this.appVersion = getAppVersion();

		this.currentOrg = getCurrentOrganization();

		const appProps = this._getAppProperties();
		window.stonal_app = appProps.app;
		window.stonal_env = appProps.stonal_env;
		window.stonal_org = this.currentOrg;
		window.stonal_stack = appProps.stonal_stack;
	}

	page (name?: string, properties?: Record<string, any>) {
		properties = this._extendProperties(properties || {});
		if (this._isOffline) {
			properties._offline = true;
			properties._offline_timestamp = new Date().toISOString();
			this._addToBuffer({
				name: name || '',
				properties: properties || {},
				type: 'page',
			});
			// Resilient destinations receive events even when offline
			this._dispatchToResilient('page', name || '', properties || {});
			return;
		}
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.page && dest.page(name || '', properties || {});
		}
	}

	identify (userId: string | undefined, traits: Record<string, any>) {
		// Store userGroups map for lookup at event time in _extendProperties
		if (traits?.userGroups) {
			this.userGroupsByOrg = traits.userGroups;
			// Persist so the constructor can eagerly restore on next page load
			try {
				localStorage.setItem('stn_tr_user_groups_by_org', JSON.stringify(traits.userGroups));
			} catch { /* ignore quota errors */ }
		}
		if (this._isOffline) {
			this._addToBuffer({
				traits,
				type: 'identify',
				userId,
			});
			this._dispatchToResilient('identify', userId, traits);
			return;
		}
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.identify && dest.identify(userId, traits);
		}
	}


	group (groupId: string, traits?: Record<string, any>) {
		// Associates the current user with a group/organization
		// Useful for B2B tracking and organization-level analysis
		if (this._isOffline) {
			this._addToBuffer({
				groupId,
				traits: traits || {},
				type: 'group',
			});
			this._dispatchToResilient('group', groupId, traits || {});
			return;
		}
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.group && dest.group(groupId, traits || {});
		}
	}

	track (event: string, properties?: Record<string, any>) {
		properties = this._extendProperties(properties);
		if (this._isOffline) {
			properties._offline = true;
			properties._offline_timestamp = new Date().toISOString();
			this._addToBuffer({
				event,
				properties,
				type: 'track',
			});
			this._dispatchToResilient('track', event, properties);
			return;
		}
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.track && dest.track(event, properties);
		}
	}

	addDestination (dest: Destination) {
		// We check if the destination is already present
		for (let i = 0; i < this.destinations.length; i++) {
			if (this.destinations[i].name === dest.name) {
				logger.error('Destination ' + dest.name + ' already present');
				return;
			}
		}
		this.destinations.push(dest);
	}

	error (event: string, properties: any) {
		logger.error(event + ' : ' + JSON.stringify(properties));
		// Track error event to all destinations with "Error: " prefix
		this.track(`Error: ${event}`, properties);
	}

	private _getAppProperties(): { app: string;
		stonal_env: string | undefined;
		stonal_stack: string | undefined } {
		// This method should only be called when this.app is defined
		// Use tracking_code if available, otherwise fall back to code
		return {
			app: this.app!.tracking_code || this.app!.code,
			stonal_env: this.app!.env,
			stonal_stack: this.app!.stack,
		};
	}

	private _extendProperties (properties: Record<string, any> | undefined): Record<string, any> {
		properties = properties || {};
		if (this.app) {
			const appProps = this._getAppProperties();
			properties.app = appProps.app;
			properties.stonal_env = appProps.stonal_env;
			if (this.currentOrg) {
				properties.current_organization = this.currentOrg;
			}
			// Resolve current user groups at event time using the latest currentOrg
			if (this.userGroupsByOrg && this.currentOrg) {
				properties.current_user_groups = this.userGroupsByOrg[this.currentOrg]
					?? Object.entries(this.userGroupsByOrg)
						.find(([
							key,
						]) => key.toLowerCase() === this.currentOrg!.toLowerCase())?.[1];
			}
			properties.stonal_stack = appProps.stonal_stack;
		}
		if (this.appVersion) {
			properties.app_version = this.appVersion;
		}
		// Inject the build version from the environment
		properties.stonal_tracking_version = typeof BUILD_VERSION !== 'undefined' ? BUILD_VERSION : 'dev';
		return properties;
	}

	/**
	 * Dispatches an event to offline-resilient destinations only.
	 * These destinations handle their own queueing/retry (e.g. RudderStack localStorage queue).
	 */
	private _dispatchToResilient(type: 'track', event: string, properties: Record<string, any>): void;
	private _dispatchToResilient(type: 'page', name: string, properties: Record<string, any>): void;
	private _dispatchToResilient(type: 'identify', userId: string | undefined, traits: Record<string, any>): void;
	private _dispatchToResilient(type: 'group', groupId: string, traits: Record<string, any>): void;
	private _dispatchToResilient(type: string, arg1: any, arg2: any): void {
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			if (!dest.offlineResilient) continue;
			switch (type) {
				case 'track':
					dest.track && dest.track(arg1, arg2);
					break;
				case 'page':
					dest.page && dest.page(arg1, arg2);
					break;
				case 'identify':
					dest.identify && dest.identify(arg1, arg2);
					break;
				case 'group':
					dest.group && dest.group(arg1, arg2);
					break;
			}
		}
	}

	/**
	 * Adds an event to the offline buffer, capping at MAX_BUFFER_SIZE
	 */
	private _addToBuffer(event: BufferedEvent): void {
		if (this.offlineBuffer.length >= MAX_BUFFER_SIZE) {
			// Drop the oldest event to make room
			this.offlineBuffer.shift();
		}
		this.offlineBuffer.push(event);
	}

	/**
	 * Flushes the offline buffer to non-resilient destinations only.
	 * Resilient destinations already received events in real-time via _dispatchToResilient.
	 */
	private _flushBuffer(): void {
		const events = [
			...this.offlineBuffer,
		];
		this.offlineBuffer = [];

		for (const bufferedEvent of events) {
			for (let i = 0; i < this.destinations.length; i++) {
				const dest = this.destinations[i];
				if (dest.offlineResilient) continue;
				switch (bufferedEvent.type) {
					case 'track':
						dest.track && dest.track(bufferedEvent.event, bufferedEvent.properties);
						break;
					case 'page':
						dest.page && dest.page(bufferedEvent.name, bufferedEvent.properties);
						break;
					case 'identify':
						dest.identify && dest.identify(bufferedEvent.userId, bufferedEvent.traits);
						break;
					case 'group':
						dest.group && dest.group(bufferedEvent.groupId, bufferedEvent.traits);
						break;
				}
			}
		}
	}
}
